/**
 * A coordinator client for places that cannot put a header on a WebSocket (browsers, Node): every event socket
 * opens with a fresh single-use ticket from `POST /v1/events/ticket`, so no long-lived token ever goes in a URL.
 *
 * In the dashboard page (WP-23) there is no token at all: the base URL is the dashboard's own same-origin proxy
 * (`/api/orgs/<org>/repos/<repo>/coordinator`), the browser's session cookie goes with every request, and the
 * dashboard Worker hands the coordinator the session as a bearer. Node callers (the scripted agents) pass
 * `tokenFor` and `ticketToken` and talk to the coordinator directly with `Authorization: Bearer <token>`.
 *
 * It wraps the contracts' `createHttpCoordinator` (validation, resume after a drop) with a fetch that adds the
 * token and a WebSocket that fetches the ticket first. Once contracts-v1.1 lands, `tokenFor` and `eventTickets`
 * on `createHttpCoordinator` replace the wrappers; the behaviour is the same.
 */
import { createHttpCoordinator, EVENTS_WS_PATH, type Coordinator, type CoordinatorOperation, type WebSocketLike } from "@botallion/contracts";

/** `POST` here, authenticated, for a single-use 60-second ticket. Mirrors the coordinator (WP-02) and contracts-v1.1. */
export const EVENTS_TICKET_PATH = "/v1/events/ticket";
/** Response header carrying a new session's token. Mirrors the coordinator (WP-02) and contracts-v1.1. */
export const SESSION_TOKEN_HEADER = "x-botallion-session-token";

export type StreamStatus = "connecting" | "open" | "closed" | "unauthorised";

export interface AuthedClientOptions {
  /** The repo's coordinator base: `<origin>/orgs/<org>/repos/<repo>`, or the dashboard proxy for that repo. */
  baseUrl: string;
  /** The bearer token for one RPC, or undefined to send none. Read on every call, never cached here. Omit in the page. */
  tokenFor?: (operation: CoordinatorOperation, input: unknown) => string | undefined;
  /**
   * The bearer token used to ask for an event ticket; when it returns undefined, no ticket is asked for. Omit it in
   * the page, where the session cookie authenticates the ticket request.
   */
  ticketToken?: () => string | undefined;
  /** Sees every RPC response, for example to collect a new session's token. */
  onResponse?: (operation: CoordinatorOperation, response: Response, input: unknown) => void;
  /** Told when the event socket connects, drops or is refused. */
  onStreamStatus?: (status: StreamStatus) => void;
  fetch?: (input: string, init: RequestInit) => Promise<Response>;
  WebSocket?: new (url: string) => WebSocketLike;
  /** Delay before reconnecting a dropped stream. Default 1000 ms; -1 disables. */
  reconnectDelayMs?: number;
}

export function createAuthedClient(options: AuthedClientOptions): Coordinator {
  const base = options.baseUrl.replace(/\/+$/, "");
  const doFetch = options.fetch ?? ((input: string, init: RequestInit) => fetch(input, init));
  const Socket = options.WebSocket ?? (globalThis as unknown as { WebSocket: new (url: string) => WebSocketLike }).WebSocket;
  const status = (s: StreamStatus) => options.onStreamStatus?.(s);

  const authedFetch = async (url: string, init: RequestInit): Promise<Response> => {
    const operation = url.slice(url.lastIndexOf("/") + 1) as CoordinatorOperation;
    let input: unknown = {};
    try {
      input = JSON.parse(String(init.body ?? "{}"));
    } catch {
      // the server answers invalid_input
    }
    const headers = new Headers(init.headers);
    const token = options.tokenFor?.(operation, input);
    if (token) headers.set("authorization", `Bearer ${token}`);
    const res = await doFetch(url, { ...init, headers });
    options.onResponse?.(operation, res, input);
    return res;
  };

  const ticket = async (): Promise<string> => {
    const token = options.ticketToken ? options.ticketToken() : null;
    if (token === undefined) throw new TicketError(401);
    const res = await doFetch(base + EVENTS_TICKET_PATH, { method: "POST", headers: token ? { authorization: `Bearer ${token}` } : {} });
    let body: { ok?: boolean; result?: { ticket?: unknown } } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      // handled below
    }
    if (!res.ok || !body.ok || typeof body.result?.ticket !== "string") throw new TicketError(res.status);
    return body.result.ticket;
  };

  /** A WebSocketLike that fetches a fresh ticket before every connection, reconnects included. */
  class TicketSocket implements WebSocketLike {
    private ws: WebSocketLike | null = null;
    private closed = false;
    private readonly handlers: Record<string, ((e: { data: unknown }) => void)[]> = {};
    constructor(url: string) {
      status("connecting");
      ticket().then(
        (t) => {
          if (this.closed) return;
          const ws = new Socket(`${url}?ticket=${encodeURIComponent(t)}`);
          this.ws = ws;
          ws.addEventListener("open", () => this.fire("open"));
          ws.addEventListener("error", () => this.fire("error"));
          ws.addEventListener("close", () => {
            status("closed");
            this.fire("close");
          });
          ws.addEventListener("message", (e) => {
            if (typeof e.data === "string" && e.data.includes('"ready"')) status("open");
            this.fire("message", e.data);
          });
        },
        (e: unknown) => {
          status(e instanceof TicketError && (e.status === 401 || e.status === 403) ? "unauthorised" : "closed");
          this.fire("close");
        },
      );
    }
    send(data: string): void {
      this.ws?.send(data);
    }
    close(): void {
      this.closed = true;
      this.ws?.close();
    }
    addEventListener(type: string, listener: (e: { data: unknown }) => void): void {
      (this.handlers[type] ??= []).push(listener);
    }
    private fire(type: string, data?: unknown): void {
      for (const h of this.handlers[type] ?? []) h({ data });
    }
  }

  return createHttpCoordinator({
    baseUrl: base,
    fetch: authedFetch,
    WebSocket: TicketSocket as unknown as new (url: string) => WebSocketLike,
    ...(options.reconnectDelayMs !== undefined ? { reconnectDelayMs: options.reconnectDelayMs } : {}),
  });
}

class TicketError extends Error {
  readonly status: number;
  constructor(status: number) {
    super(`could not get an event ticket (HTTP ${status})`);
    this.status = status;
  }
}

/** The event socket URL for a base URL, as `createHttpCoordinator` builds it. Used for the page's CSP check. */
export function eventsUrl(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, "").replace(/^http/, "ws") + EVENTS_WS_PATH;
}
